import { z } from "zod";

import { mediaSchema, zBool, zDecimal, zId, zNumber, zText, zTimestamp } from "@/shared/api/schema";

const namedRefSchema = z.looseObject({
  id: zId,
  name: z.string(),
  media: z.array(mediaSchema).default([]),
});

const creatorSchema = z.looseObject({
  id: zId,
  firstname: zText,
  lastname: zText,
  email: zText,
  roles: z.array(z.string()).default([]),
});

/** The marketing flag on a unit ("Hot", "Under market price", "Need to sell fast"). */

export const unitSchema = z.looseObject({
  id: zId,
  unit_code: z.string(),
  project_id: zId.nullable(),
  developer_id: zId.nullable(),
  phase: zText,
  unit_number: zText,
  unit_type: zText,
  total_price: zDecimal,
  size: zDecimal,
  dp: zDecimal,
  remaining: zDecimal,
  beds: zNumber,
  bathroom: zNumber,
  bua: zNumber,
  finishing: zText,
  additional: zText,
  tags: zText,
  property_features: zText,
  /** Internal notes: never shown to clients (offers leave them out). */
  notes: zText,
  property_size: zText,
  land_area: zText,
  status_tags: zText,
  is_sold: zBool,
  /** Held for a buyer, not sold yet; never true together with is_sold. */
  is_reserved: zBool.optional().default(false),
  created_by: zId.nullable(),
  created_at: zTimestamp.optional(),
  updated_at: zTimestamp,
  developer: namedRefSchema.nullish(),
  project: namedRefSchema.nullish(),
  media: z.array(mediaSchema).default([]),
  creator: creatorSchema.nullish(),
});
export type Unit = z.infer<typeof unitSchema>;

const approverSchema = z.looseObject({ id: zId, name: zText, email: zText });

export const unitDetailSchema = unitSchema.extend({ last_approver: approverSchema.nullish() });
export type UnitDetail = z.infer<typeof unitDetailSchema>;

export const activitySchema = z.looseObject({
  id: zId,
  description: zText,
  event: zText,
  created_at: zTimestamp,
  causer: z.looseObject({ id: zId, firstname: zText, lastname: zText }).nullish(),
  properties: z
    .looseObject({
      attributes: z.record(z.string(), z.unknown()).optional(),
      old: z.record(z.string(), z.unknown()).optional(),
    })
    .nullish()
    .transform((value) => value ?? {}),
});
export type Activity = z.infer<typeof activitySchema>;

export const commentSchema = z.looseObject({
  id: zId,
  comment: z.string(),
  user_id: zId,
  created_at: zTimestamp,
  user: z
    .looseObject({
      id: zId,
      firstname: zText.optional(),
      lastname: zText.optional(),
      name: zText.optional(),
    })
    .nullish(),
});
export type UnitComment = z.infer<typeof commentSchema>;

export const CLAIM_STATUSES = ["pending", "approved", "rejected"] as const;
export type ClaimStatus = (typeof CLAIM_STATUSES)[number];

export const claimSchema = z.looseObject({
  id: zId,
  inventory_id: zId,
  user_id: zId,
  comment: zText,
  status: z.enum(CLAIM_STATUSES).catch("pending"),
  created_at: zTimestamp.optional(),
  updated_at: zTimestamp,
  user: z
    .looseObject({
      id: zId,
      name: zText.optional(),
      firstname: zText.optional(),
      lastname: zText.optional(),
      email: zText.optional(),
    })
    .nullish(),
  inventory: z.looseObject({ id: zId, unit_code: zText }).nullish(),
});
export type Claim = z.infer<typeof claimSchema>;

/** A person's display name from either { name } or { firstname, lastname }. */
export function personName(
  person:
    | { name?: string | null; firstname?: string | null; lastname?: string | null }
    | null
    | undefined,
): string {
  if (!person) return "";
  if (person.name) return person.name;
  return [person.firstname, person.lastname].filter(Boolean).join(" ");
}

/** The best picture for a list or card: the generated thumbnail if there is one. */
export function coverOf(unit: Pick<Unit, "media">): { thumb: string | null; full: string | null } {
  const first = unit.media[0];
  if (!first) return { thumb: null, full: null };
  return { thumb: first.preview_url || first.original_url, full: first.original_url };
}

/** "Apartment · Mivida", the unit's headline. */
export function unitTitle(unit: Pick<Unit, "unit_type" | "project">): string {
  return [unit.unit_type, unit.project?.name].filter(Boolean).join(" · ");
}
